import { useEffect, useRef, useState } from 'react'
import { Head } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Play, Loader2, CheckCircle2, XCircle, Copy, Check, AlertTriangle, Terminal } from 'lucide-react'
import { ConsoleEvent, RunResult, runTier1Scenario } from '@/lib/test-demo/scenarios'
import { Language, snippets } from '@/lib/test-demo/code-snippets'
import { MediaTestSection } from '@/components/test-demo/MediaTestSection'

interface Props {
  configured: boolean
  base_path: string
}

const levelColor: Record<ConsoleEvent['level'], string> = {
  info: 'text-zinc-400',
  request: 'text-cyan-400',
  response: 'text-emerald-400',
  success: 'text-emerald-400',
  error: 'text-red-400',
}

function formatTime(ts: number): string {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}.${String(d.getMilliseconds()).padStart(3, '0')}`
}

function ConsoleStream({ events }: { events: ConsoleEvent[] }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight })
  }, [events.length])

  return (
    <div ref={ref} className="font-mono text-xs leading-relaxed bg-zinc-950 rounded-lg p-4 h-[480px] overflow-y-auto">
      {events.length === 0 ? (
        <div className="text-zinc-500 flex items-center gap-2">
          <Terminal className="h-3.5 w-3.5" />
          Press &ldquo;Run live test&rdquo; to start.
        </div>
      ) : (
        events.map((event, i) => (
          <div key={i} className="mb-2">
            <span className="text-zinc-600 mr-2">[{formatTime(event.ts)}]</span>
            <span className={levelColor[event.level]}>{event.text}</span>
            {event.detail ? <pre className="text-zinc-500 ml-12 mt-1 whitespace-pre-wrap break-all">{event.detail}</pre> : null}
          </div>
        ))
      )}
    </div>
  )
}

function CodePanel() {
  const [language, setLanguage] = useState<Language>('curl')
  const [copiedStep, setCopiedStep] = useState<number | null>(null)

  function copy(code: string, step: number) {
    navigator.clipboard.writeText(code)
    setCopiedStep(step)
    setTimeout(() => setCopiedStep(null), 2000)
  }

  return (
    <div className="space-y-3">
      <Tabs value={language} onValueChange={(v) => setLanguage(v as Language)}>
        <TabsList className="grid grid-cols-3 w-fit">
          <TabsTrigger value="js">JavaScript</TabsTrigger>
          <TabsTrigger value="python">Python</TabsTrigger>
          <TabsTrigger value="curl">curl</TabsTrigger>
        </TabsList>
        {(['js', 'python', 'curl'] as Language[]).map((lang) => (
          <TabsContent key={lang} value={lang} className="space-y-3 mt-4">
            {snippets.map((s, i) => (
              <div key={s.step}>
                <div className="flex items-center justify-between mb-1">
                  <code className="text-xs text-muted-foreground font-mono">{s.step}</code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                    onClick={() => copy(s[lang], i)}
                  >
                    {copiedStep === i ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
                <pre className="overflow-x-auto rounded-lg bg-zinc-950 p-3 text-xs leading-relaxed">
                  <code className="font-mono text-zinc-300">{s[lang]}</code>
                </pre>
              </div>
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

export default function TestDemo({ configured, base_path }: Props) {
  const [events, setEvents] = useState<ConsoleEvent[]>([])
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<RunResult | null>(null)

  const push: (event: Omit<ConsoleEvent, 'ts'>) => void = (event) => {
    setEvents((prev) => [...prev, { ...event, ts: Date.now() }])
  }

  async function runTest() {
    setRunning(true)
    setResult(null)
    setEvents([])
    push({ level: 'info', text: 'Starting Tier 1 signaling smoke test against the live API.' })

    try {
      const r = await runTier1Scenario({ basePath: base_path, push })
      setResult(r)
      push({ level: r.ok ? 'success' : 'error', text: r.ok ? 'All steps passed.' : 'Test failed — see error above.' })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      push({ level: 'error', text: `Unexpected error: ${message}` })
    } finally {
      setRunning(false)
    }
  }

  return (
    <>
      <Head>
        <title>Live API Test — KonexZero</title>
        <meta name="description" content="Run a real end-to-end signaling test against the KonexZero API in your browser. See every request and response live, with copy-paste code." />
      </Head>
      <PageLayout maxWidth="7xl">
        <div className="mb-8 space-y-3">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Terminal className="h-3.5 w-3.5" />
            Live test
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            See the API for yourself
          </h1>
          <p className="max-w-2xl text-lg text-muted-foreground">
            Press a button and the page runs real signaling calls against{' '}
            <code className="font-mono text-sm">api.konexzero.com</code> on a sandboxed credential. Watch the live console
            on the left, copy the code on the right.
          </p>
        </div>

        {!configured && (
          <Alert variant="default" className="mb-6 border-amber-500/40">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Demo not configured for this environment</AlertTitle>
            <AlertDescription>
              Set <code>TEST_DEMO_APP_ID</code> and <code>TEST_DEMO_API_KEY</code> on the server and the live demo will activate.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="surface-card space-y-4 p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold tracking-tight">Live console</h2>
              <div className="flex items-center gap-2">
                {result ? (
                  <Badge
                    variant="outline"
                    className={
                      result.ok
                        ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                        : 'border-red-500/40 text-red-600 dark:text-red-400'
                    }
                  >
                    {result.ok ? `✓ ${result.steps.length} passed` : `✗ ${result.steps.filter((s) => !s.ok).length} failed`}
                  </Badge>
                ) : null}
                <Button onClick={runTest} disabled={running || !configured}>
                  {running ? (
                    <>
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      Running…
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-3.5 w-3.5" />
                      Run live test
                    </>
                  )}
                </Button>
              </div>
            </div>
            <ConsoleStream events={events} />
            {result ? (
              <div className="grid grid-cols-1 gap-1 pt-3">
                {result.steps.map((step) => (
                  <div key={step.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      {step.ok ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-red-500" />
                      )}
                      <span className="font-mono">{step.name}</span>
                    </div>
                    <span className="text-muted-foreground">{step.durationMs}ms</span>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <section className="surface-card space-y-4 p-6">
            <h2 className="font-heading text-base font-semibold tracking-tight">
              The exact code being run
            </h2>
            <CodePanel />
          </section>
        </div>

        {configured ? (
          <div className="mt-6">
            <MediaTestSection basePath={base_path} push={push} />
          </div>
        ) : null}

        <div className="surface-card mt-12 p-6 text-sm text-muted-foreground">
          <p>
            This page calls a server-side proxy at <code>{base_path}/*</code> that injects a sandbox credential into every
            request. The secret never reaches the browser. The proxy is rate-limited per IP. Need to test with your own
            credentials?{' '}
            <a className="underline underline-offset-4 hover:text-foreground" href="https://app.konexzero.com">Sign up</a>{' '}
            and read the{' '}
            <a className="underline underline-offset-4 hover:text-foreground" href="/docs">API docs</a>.
          </p>
        </div>
      </PageLayout>
    </>
  )
}
